import { ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import DatePicker from 'components/shared/form/DatePicker';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import { parseDate, simpleDashDate } from 'lib/date';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import PencilIcon from 'svg/PencilIcon';

import { ReferralFieldDef } from '../constants';

interface Option {
  label: string;
  value: string;
}

interface Props {
  field: ReferralFieldDef;
  value: string;
  onChange: (value: string) => void;
  options?: Option[];
  error?: string;
  // How the value reads when NOT being edited — the SSN passes its mask through here. Only the read
  // state is decorated: the edit control always shows the raw value, because asking someone to correct
  // a number they cannot read is the bug, not the feature.
  renderValue?: (value: string) => ReactNode;
}

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
`;

const Label = styled(BodySmall)`
  color: ${colors.black50};
  flex: 0 0 40%;
`;

const ValueCell = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Placeholder = styled(BodySmall)`
  color: ${colors.black25};
`;

const EditButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
`;

const ErrorText = styled(BodySmall)`
  color: ${colors.red100};
`;

const Confirm = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  align-self: flex-start;
  color: ${colors.blue100};
  text-decoration: underline;
`;

/**
 * One reviewable field: reads as a label/value row with a pencil, and swaps to the right control for
 * its type when edited (OLIO-2846). Net-new because every other PencilIcon in the app opens a modal or
 * slideout — this edits in place, inside a modal that is itself the review surface.
 *
 * EXIT IS EXPLICIT — confirm, Escape, or Enter — and NEVER on blur. Blur-to-commit is the classic bug
 * for a row like this: clicking a Select's menu option blurs the input, which would unmount the control
 * before the selection landed, so picking a state from the dropdown would silently do nothing.
 */
export default function EditableField({ field, value, onChange, options, error, renderValue }: Props) {
  // `ssn` is rendered as a plain text box here, NOT via the entry form's SsnField (OLIO-2586).
  // Deliberate, and worth revisiting: SsnField stores digits-only and shields a packet partial behind
  // an explicit "enter the full number" gesture, which is arguably better than this field's
  // reveal-and-correct treatment of a redacted value. But adopting it changes this modal's SSN
  // contract (mask-while-reading, reveal-while-invalid, clear-to-unblock) rather than merely swapping
  // a control, so it is a follow-up rather than something to smuggle into a merge.
  const isTextual = field.type === 'text' || field.type === 'ssn';
  // Always starts read-only. This used to open in edit mode for the field a vendor had rejected —
  // always the MRN in practice, which is the one field the coordinator no longer supplies (it is
  // facility configuration now, generated by the platform). With no field left that a reopen should
  // land on, the review step shows every value read-only and the coordinator picks the one to change.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Re-seed when the committed value changes underneath (a refetch, or a reset on reopen) — but only
  // while not editing, so a refetch mid-edit can't yank what someone is typing.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing && isTextual) inputRef.current?.focus();
  }, [editing, isTextual]);

  const commit = (next: string) => {
    onChange(next);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      commit(draft);
    }
  };

  if (!editing) {
    return (
      <Row data-testid={`editable-field-${field.name}`}>
        <Label>
          {field.label}
          {field.optional ? ' (optional)' : ''}
        </Label>
        <ValueCell>
          <ReadRow>
            {value ? (
              <BodySmallBold data-testid={`value-${field.name}`}>
                {renderValue ? renderValue(value) : value}
              </BodySmallBold>
            ) : (
              <Placeholder data-testid={`value-${field.name}`}>Not provided</Placeholder>
            )}
            <EditButton
              type='button'
              onClick={() => setEditing(true)}
              data-testid={`edit-${field.name}`}
              aria-label={`Edit ${field.label}`}>
              <PencilIcon />
            </EditButton>
          </ReadRow>
          {error && <ErrorText data-testid={`error-${field.name}`}>{error}</ErrorText>}
        </ValueCell>
      </Row>
    );
  }

  return (
    <Row data-testid={`editable-field-${field.name}`}>
      <Label>
        {field.label}
        {field.optional ? ' (optional)' : ''}
      </Label>
      <ValueCell onKeyDown={onKeyDown}>
        {isTextual && (
          <Input
            ref={inputRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            data-testid={`field-${field.name}`}
          />
        )}

        {field.type === 'select' && (
          <div data-testid={`field-${field.name}`}>
            <Select
              inputId={`field-${field.name}`}
              placeholder='Select'
              options={options ?? []}
              value={options?.find((option) => option.value === draft) ?? null}
              // Commits immediately: a select has no separate confirm gesture, and requiring one after
              // picking from a menu reads as the pick not having registered.
              onChange={(option) => commit((option as Option | null)?.value ?? '')}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
            />
          </div>
        )}

        {field.type === 'date' && (
          <DatePicker
            selected={parseDate(draft) ?? null}
            onChange={(date) => commit((date ? simpleDashDate(date) : '') ?? '')}
            $fullWidth
            placeholder='MM/DD/YYYY'
          />
        )}

        {isTextual && (
          <Confirm type='button' onClick={() => commit(draft)} data-testid={`confirm-${field.name}`}>
            Done
          </Confirm>
        )}

        {error && <ErrorText data-testid={`error-${field.name}`}>{error}</ErrorText>}
      </ValueCell>
    </Row>
  );
}
